import classNames from 'classnames';
import React from 'react';
import { useParams } from 'react-router-dom';
import Button from '@atlaskit/button';
import Drawer from '@atlaskit/drawer';
import ArrowLeftCircleIcon from '@atlaskit/icon/glyph/arrow-left-circle';
import Spinner from '@atlaskit/spinner';
import { AnalyticsEventSource } from '@atlassian/temp-analytics';
import { PricingPlanCycle } from '@atlassian/cssxp-ccp-types';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
import { SkeletonLoader, SkeletonLoaderWrapper } from '@css-xp/skeleton-loader';
import { AppContent, AppPage, ErrorComponents, useFeatureToggle, useSidebar } from '@css-xp/base';
// @ts-ignore
import CatalogLogo from '@css-xp/base/assets/img/catalog-logo.png';
// @ts-ignore
import CatalogGroupLogo from '@css-xp/base/assets/img/catalog-group-logo.svg';
import { useApplicationContext } from '@css-xp/base/common-context';
import { useFetchNativeProductsForCatalogue } from '@css-xp/ppc-api';
import {
  CATALOGUE_PAGE_SIDEBARS,
  initialBillingState,
  initialFilterState,
  initialIsTableLoading,
  initialQuerySearch,
  initialSearchResults,
  OrderReviewWithProvisioning,
  PartnerCatalogueProvider,
  PartnerCatalogueProviderProviderConfig,
  PartnerQuoteCatalogue,
  PartnerQuoteLineItems,
  PreCheckoutSummary,
  ProductsHeader,
  ProductsTable,
  usePartnerCatalogueContext,
} from '@css-xp/ppc-checkout';
import { TableLoader } from '@css-xp/ppc-page-loaders';
import {
  PPC_PAGE_TITLE,
  ENABLE_DESIGN_ITERATION_1,
  STATSIG_FEATURE_GATES,
  CATALOG_PAGE_LOADER_OPTIONS,
} from '@css-xp/ppc-constants';
import PartnerProductCatalogueContext from '@css-xp/ppc-checkout/src/products-in-catalogue/contexts/product-catalogue-context';
import { useTitle, useOpenClose } from '@css-xp/ppc-custom-hooks';
import { withErrorWrapper } from '@css-xp/ppc-wrapper';
import { ScreenEventAnalytics } from '@css-xp/ppc-common';
import { DiscountTierType, PARTNER_DISCOUNT_TIER_PROMOTION_ID_MAPPING } from './constants/partner-promotions';
import { ProductPricingPane } from './sidepane';
import './catalogue-page.scss';

interface CataloguePageContentProps {
  txaId: string;
  isOpen: boolean;
  close: () => void;
  renderCatalogMessage: () => JSX.Element;
  renderOrderReview: () => JSX.Element;
}

const allowedSidebars = {
  [CATALOGUE_PAGE_SIDEBARS.PRODUCT_PRICING_PLAN]: ProductPricingPane,
};

const getPromotionId = (discountTier: DiscountTierType): string | null => {
  const applicableDiscountTier = Object.keys(PARTNER_DISCOUNT_TIER_PROMOTION_ID_MAPPING).find((tier) =>
    discountTier.includes(tier)
  );
  return applicableDiscountTier
    ? PARTNER_DISCOUNT_TIER_PROMOTION_ID_MAPPING[applicableDiscountTier as DiscountTierType]
    : null;
};

const DrawerBackButton = ({ close }: { close: () => void }) => {
  return (
    <Button
      iconBefore={<ArrowLeftCircleIcon label="left-arrow" />}
      appearance="subtle"
      onClick={close}
      className="order-review-back-button"
    >
      Back
    </Button>
  );
};

const CataloguePageContent = ({
  isOpen,
  renderCatalogMessage,
  close,
  renderOrderReview,
}: CataloguePageContentProps) => {
  const [isEnabled] = useFeatureToggle();
  const isDesignIteration1Enabled = isEnabled(ENABLE_DESIGN_ITERATION_1);
  const isAppificationEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_APPIFICATION);
  const { isSidebarOpen } = useSidebar();

  const getDrawerBackButton = () => {
    return <DrawerBackButton close={close} />;
  };

  const { isPartnerCatalogueInformationLoading, quoteItems } = usePartnerCatalogueContext();
  const filterOptions = [
    {
      key: 'includeProperties',
      values: ['vendorName'],
    },
  ];
  return (
    <>
      <div>
        {isPartnerCatalogueInformationLoading &&
          (isDesignIteration1Enabled ? (
            <SkeletonLoaderWrapper>
              <SkeletonLoader />
            </SkeletonLoaderWrapper>
          ) : (
            <div className="spinner-container">
              <Spinner size={'large'} />
            </div>
          ))}
      </div>
      <div
        className={classNames('catalog-page-old-container', {
          'blur-background': isPartnerCatalogueInformationLoading,
        })}
      >
        {!isDesignIteration1Enabled && (
          <div className="products-table-container">
            <ProductsHeader filterOptions={filterOptions} />
          </div>
        )}
        <div className="main-content-container">
          <ProductsTable
            isJWMDisabled
            isAtlasDisabled
            isEnterpriseOfferingEnabled={true}
            isJSMRepackagingEnabled={isEnabled(STATSIG_FEATURE_GATES.ENABLE_JSM_OFFERING_CHANGE)}
            isCompassPremiumEnabled={isEnabled(STATSIG_FEATURE_GATES.ENABLE_COMPASS_PREMIUM)}
            isJPDAsJiraFamilyEnabled={isEnabled(STATSIG_FEATURE_GATES.ENABLE_JPD_AS_JIRA_FAMILY)}
            {...(isDesignIteration1Enabled && { version: 2 })}
            isAppificationEnabled={isAppificationEnabled}
          />
          {!isDesignIteration1Enabled && !(quoteItems.length > 0) && !isSidebarOpen && renderCatalogMessage()}
          <PartnerQuoteCatalogue />
        </div>
        <Drawer
          overrides={{
            Sidebar: {
              component: getDrawerBackButton,
            },
          }}
          isOpen={isOpen}
          onClose={close}
          width="full"
        >
          {renderOrderReview()}
        </Drawer>
      </div>
    </>
  );
};

export const CataloguePage = () => {
  const [isEnabled] = useFeatureToggle();
  const { loading, data, error } = useFetchNativeProductsForCatalogue();
  const [billingFrequencyState, setCurrentBillingState] = React.useState(initialBillingState);
  const [filterState, setFilter] = React.useState(initialFilterState);
  const [isTableLoading, setIsTableLoading] = React.useState(initialIsTableLoading);
  const [searchResults, setSearchResults] = React.useState(initialSearchResults);
  const [querySearch, setQuerySearch] = React.useState(initialQuerySearch);
  const { isOpen, open, close } = useOpenClose();
  const [lineItems, setLineItems] = React.useState<PartnerQuoteLineItems>([]);
  useTitle(PPC_PAGE_TITLE.CATALOG);
  const context = useApplicationContext();

  const { txaId } = useParams<{ txaId: string }>();
  const isDesignIteration1Enabled = isEnabled(ENABLE_DESIGN_ITERATION_1);

  const partnerAccount = context?.partnerAccounts?.find(
    (account: { transactionAccountId: string; discountTier: string }) => account.transactionAccountId === txaId
  );

  const discountTier = partnerAccount?.discountTier ?? '';
  const applicablePromotionId = getPromotionId(discountTier);

  const renderError = () => {
    if (error) {
      const GenericError = withErrorWrapper(ErrorComponents.GenericErrorPage);
      return <GenericError error={error} />;
    }
  };

  const productCatalogueValue = {
    billingFrequencyState,
    setCurrentBillingState,
    filterState,
    setFilter,
    isTableLoading,
    setIsTableLoading,
    querySearch,
    setQuerySearch,
    searchResults,
    setSearchResults,
    loading,
    data,
    error,
  };

  const catalogueQuoteConfig: PartnerCatalogueProviderProviderConfig = {
    isCreateQuoteEnabled: true,
    pricingPlanCycle: PricingPlanCycle.MONTHLY,
  };

  const renderCatalogMessage = () => {
    return (
      <div className="catalogue-message-old">
        <img src={CatalogLogo} className="catalogue-logo-old" />
        <div className="catalogue-heading">
          <div className="atlassian-book-container">
            {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
            {/* @ts-ignore */}
            <img src={CatalogGroupLogo} />
          </div>
          <h1 className="catalogue-title">Welcome to Atlassian Catalog</h1>
          <div className="description-container">
            <p className="description">You'll find all the latest pricing plans, pricing changes, and more.</p>
          </div>
        </div>
      </div>
    );
  };
  const renderPageContent = () => {
    return (
      <CataloguePageContent
        txaId={txaId}
        isOpen={isOpen}
        renderCatalogMessage={renderCatalogMessage}
        close={close}
        renderOrderReview={() => {
          return (
            <OrderReviewWithProvisioning
              lineItems={lineItems}
              transactionAccountId={txaId}
              applicablePromotionId={applicablePromotionId}
              onClose={close}
            />
          );
        }}
      />
    );
  };

  const renderAppContent = () => {
    const filterOptions = [
      {
        key: 'includeProperties',
        values: ['vendorName'],
      },
    ];
    if (isDesignIteration1Enabled) {
      return (
        <div
          className={classNames('catalog-page-old-container', {
            'blur-background': loading && !isDesignIteration1Enabled,
          })}
        >
          <div className="products-table-container">
            <ProductsHeader filterOptions={filterOptions} hideSearchBar={loading && isDesignIteration1Enabled} />
          </div>
          {loading ? (
            <TableLoader {...CATALOG_PAGE_LOADER_OPTIONS} hideSearchBar={false} />
          ) : (
            renderError() ?? renderPageContent()
          )}
        </div>
      );
    }
    return loading ? <LoadingIndicator /> : renderError() ?? renderPageContent();
  };

  return (
    <PartnerProductCatalogueContext.Provider value={productCatalogueValue}>
      <PartnerCatalogueProvider config={catalogueQuoteConfig}>
        <AppPage className={'catalogue-page-overrides'} allowedSidebars={allowedSidebars}>
          <ScreenEventAnalytics
            event={{
              name: AnalyticsEventSource.PARTNER_CATALOG_SCREEN,
              attributes: {},
            }}
          />
          <AppContent className={!loading ? 'app-content-products' : ''}>{renderAppContent()}</AppContent>
          <PreCheckoutSummary
            transactionAccountId={txaId}
            applicablePromotionId={applicablePromotionId ?? ''}
            onCheckout={(lineItems: PartnerQuoteLineItems) => {
              setLineItems(lineItems);
              open();
            }}
          />
        </AppPage>
      </PartnerCatalogueProvider>
    </PartnerProductCatalogueContext.Provider>
  );
};
